import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Form, Nav, Spinner } from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminPruebasPage.css';
import { logError } from '../../utils/logger';

const TAB_MAILING = 'mailing';
const TAB_PRODUCTO = 'producto';

const TEMPLATE_UI = {
  purchase_demo: { icon: 'bi-bag-check-fill', label: 'Compra' },
  novedades_bloque: { icon: 'bi-megaphone-fill', label: 'Novedades' },
  recordatorio_simple: { icon: 'bi-bell-fill', label: 'Recordatorio' },
  staff_notify_demo: { icon: 'bi-inboxes-fill', label: 'Reenvío interno' },
};

const AdminPruebasInner = () => {
  const { user, token } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  const canTester = useMemo(() => hasPermission(user, PERMISSION_IDS.TESTER), [user]);

  const [activeTab, setActiveTab] = useState(TAB_MAILING);
  const [emailQuery, setEmailQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  const [grantCourses, setGrantCourses] = useState([]);
  const [loadingGrantCourses, setLoadingGrantCourses] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [grantingCourse, setGrantingCourse] = useState(false);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token],
  );

  useEffect(() => {
    if (user && !canTester) {
      notify({
        title: 'Acceso denegado',
        message: 'Necesitás el permiso Tester para usar el panel Pruebas.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, canTester, navigate, notify]);

  const loadTemplates = useCallback(async () => {
    if (!token || !canTester) return;
    setLoadingTemplates(true);
    try {
      const res = await fetch(buildApiUrl('/api/user/tester/mailing/templates'), {
        headers: authHeaders,
      });
      if (res.status === 403) {
        notify({ title: 'Sin permiso', message: 'No tenés permiso Tester.', variant: 'error' });
        setTemplates([]);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
    } catch (e) {
      logError('[AdminPruebas] templates', e);
      notify({
        title: 'Error',
        message: 'No se pudieron cargar las plantillas de correo.',
        variant: 'error',
      });
    } finally {
      setLoadingTemplates(false);
    }
  }, [token, canTester, authHeaders, notify]);

  const loadGrantCourses = useCallback(async () => {
    if (!token || !canTester) return;
    setLoadingGrantCourses(true);
    try {
      const res = await fetch(buildApiUrl('/api/user/tester/grant/courses'), {
        headers: authHeaders,
      });
      if (res.status === 403) {
        notify({ title: 'Sin permiso', message: 'No tenés permiso Tester.', variant: 'error' });
        setGrantCourses([]);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setGrantCourses(Array.isArray(data.courses) ? data.courses : []);
    } catch (e) {
      logError('[AdminPruebas] grant courses', e);
      notify({
        title: 'Error',
        message: 'No se pudo cargar el listado de cursos.',
        variant: 'error',
      });
    } finally {
      setLoadingGrantCourses(false);
    }
  }, [token, canTester, authHeaders, notify]);

  useEffect(() => {
    if (canTester) {
      loadTemplates();
      loadGrantCourses();
    }
  }, [canTester, loadTemplates, loadGrantCourses]);

  const handleSearchUser = async (e) => {
    e.preventDefault();
    const q = emailQuery.trim();
    if (!q) {
      notify({ title: 'Email', message: 'Ingresá un email para buscar.', variant: 'warning' });
      return;
    }
    setSearching(true);
    setTargetUser(null);
    try {
      const url = `${buildApiUrl('/api/user/tester/mailing/user')}?email=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        notify({ title: 'No encontrado', message: 'No hay usuario con ese email.', variant: 'warning' });
        return;
      }
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setTargetUser(data);
      notify({ title: 'Listo', message: `Usuario encontrado: ${data.email}`, variant: 'success' });
    } catch (err) {
      logError('[AdminPruebas] search', err);
      notify({
        title: 'Error',
        message: err.message || 'No se pudo buscar el usuario.',
        variant: 'error',
      });
    } finally {
      setSearching(false);
    }
  };

  const handleSendSample = async (templateId) => {
    if (!targetUser?.id) {
      notify({
        title: 'Destinatario',
        message: 'Buscá primero un usuario por email.',
        variant: 'warning',
      });
      return;
    }
    setSendingId(templateId);
    try {
      const res = await fetch(buildApiUrl('/api/user/tester/mailing/send-sample'), {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ userId: targetUser.id, templateId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 503) {
        notify({
          title: 'SMTP',
          message: data.error || 'Correo no configurado en el servidor.',
          variant: 'error',
        });
        return;
      }
      if (!res.ok) {
        throw new Error(data.error || data.details || `HTTP ${res.status}`);
      }
      notify({
        title: 'Enviado',
        message: `Correo de prueba enviado a ${data.to || targetUser.email}`,
        variant: 'success',
      });
    } catch (err) {
      logError('[AdminPruebas] send', err);
      notify({
        title: 'Error al enviar',
        message: err.message || 'Falló el envío.',
        variant: 'error',
      });
    } finally {
      setSendingId(null);
    }
  };

  const handleGrantCourse = async () => {
    if (!targetUser?.id) {
      notify({
        title: 'Destinatario',
        message: 'Buscá primero un usuario por email.',
        variant: 'warning',
      });
      return;
    }
    const courseId = parseInt(String(selectedCourseId), 10);
    if (!courseId || Number.isNaN(courseId)) {
      notify({ title: 'Curso', message: 'Elegí un curso de la lista.', variant: 'warning' });
      return;
    }
    setGrantingCourse(true);
    try {
      const res = await fetch(buildApiUrl('/api/user/tester/grant/course'), {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ userId: targetUser.id, courseId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        notify({
          title: 'Sin cambios',
          message: data.error || 'Ese usuario ya tenía el curso.',
          variant: 'warning',
        });
        return;
      }
      if (!res.ok) {
        throw new Error(data.error || data.details || `HTTP ${res.status}`);
      }
      const mailOk = Boolean(data.emailSent);
      const mailHint = data.mailWarning
        ? ` ${data.mailWarning}`
        : '';
      const mailLine = mailOk
        ? ` Se envió el mismo correo de confirmación de compra que tras un pago (orden ${data.orderNumber || '—'}).`
        : mailHint;
      notify({
        title: mailOk ? 'Listo' : 'Curso asignado',
        message: `“${data.courseName || 'Curso'}” quedó habilitado para ${targetUser.email}.${mailLine}`,
        variant: mailOk ? 'success' : data.mailWarning ? 'warning' : 'success',
      });
    } catch (err) {
      logError('[AdminPruebas] grant course', err);
      notify({
        title: 'Error',
        message: err.message || 'No se pudo asignar el curso.',
        variant: 'error',
      });
    } finally {
      setGrantingCourse(false);
    }
  };

  if (!user || !canTester) {
    return null;
  }

  return (
    <div className="admin-page">
      <AdminSidebar
        activeKey="pruebas"
        onNavigate={(item) => {
          if (item.path) navigate(item.path);
        }}
      />
      <div className="admin-content">
        <div className="admin-header admin-pruebas-header">
          <div className="admin-pruebas-header-text">
            <h1 className="admin-title mb-1">
              <span className="admin-pruebas-header-icon" aria-hidden>
                <i className="bi bi-flask" />
              </span>
              Pruebas
            </h1>
            <p className="admin-subtitle mb-0">
              Herramientas de QA · requiere permiso <span className="admin-pruebas-badge">Tester</span>
            </p>
          </div>
        </div>

        <div className="admin-pruebas-tabbar-wrap">
          <Nav
            variant="pills"
            className="admin-pruebas-tabs"
            activeKey={activeTab}
            onSelect={(k) => k && setActiveTab(k)}
          >
            <Nav.Item>
              <Nav.Link className="admin-pruebas-tab-link" eventKey={TAB_MAILING}>
                <i className="bi bi-envelope-paper-fill me-2" aria-hidden />
                Mailing
                <span className="admin-pruebas-tab-hint d-none d-md-inline">Correos de muestra</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link className="admin-pruebas-tab-link" eventKey={TAB_PRODUCTO}>
                <i className="bi bi-mortarboard-fill me-2" aria-hidden />
                Producto
                <span className="admin-pruebas-tab-hint d-none d-md-inline">Asignar curso</span>
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        <div className="admin-pruebas-max">
          <section className="admin-pruebas-panel admin-pruebas-panel--search">
            <div className="admin-pruebas-panel-head">
              <h2 className="admin-pruebas-panel-title">
                <i className="bi bi-person-search me-2 text-primary" aria-hidden />
                Destinatario
              </h2>
              <p className="admin-pruebas-panel-lead">
                Buscá un usuario registrado; aplica a mailing de prueba y a la asignación de cursos.
              </p>
            </div>
            <Form onSubmit={handleSearchUser} className="admin-pruebas-search-form">
              <Form.Group className="admin-pruebas-search-field">
                <Form.Label className="text-secondary small fw-semibold text-uppercase letter-spacing-tight">
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  size="lg"
                  placeholder="nombre@dominio.com"
                  value={emailQuery}
                  onChange={(ev) => setEmailQuery(ev.target.value)}
                  autoComplete="off"
                  className="admin-pruebas-input"
                />
              </Form.Group>
              <div className="admin-pruebas-search-actions">
                <Button type="submit" variant="primary" size="lg" className="admin-pruebas-btn-search" disabled={searching}>
                  {searching ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Buscando…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-search me-2" aria-hidden />
                      Buscar usuario
                    </>
                  )}
                </Button>
              </div>
            </Form>
            {targetUser ? (
              <div className="admin-pruebas-recipient">
                <div className="admin-pruebas-recipient-avatar" aria-hidden>
                  {(targetUser.nombre || '?').trim().charAt(0).toUpperCase()}
                </div>
                <div className="admin-pruebas-recipient-body">
                  <div className="admin-pruebas-recipient-label">Usuario seleccionado</div>
                  <div className="admin-pruebas-recipient-name">
                    {targetUser.nombre} {targetUser.apellido}
                  </div>
                  <div className="admin-pruebas-recipient-email">{targetUser.email}</div>
                  <span className="admin-pruebas-recipient-id">ID {targetUser.id}</span>
                </div>
              </div>
            ) : (
              <p className="admin-pruebas-empty-hint text-muted mb-0">
                <i className="bi bi-info-circle me-1" aria-hidden />
                Todavía no hay usuario seleccionado. Buscá un email para habilitar las acciones de prueba.
              </p>
            )}
          </section>
        </div>

        {activeTab === TAB_MAILING && (
          <div className="admin-pruebas-mailing">
            <div className="admin-pruebas-max">
              <section className="admin-pruebas-panel admin-pruebas-panel--templates">
                <div className="admin-pruebas-panel-head admin-pruebas-panel-head--row">
                  <div>
                    <h2 className="admin-pruebas-panel-title mb-1">
                      <i className="bi bi-layers-half me-2 text-primary" aria-hidden />
                      Plantillas de prueba
                    </h2>
                    <p className="admin-pruebas-panel-lead mb-0">
                      Cada fila es un tipo de correo distinto. El botón envía al usuario encontrado arriba.
                    </p>
                  </div>
                  {loadingTemplates && <Spinner animation="border" className="text-primary" />}
                </div>

                {!loadingTemplates && templates.length === 0 && (
                  <p className="text-muted mb-0">No hay plantillas disponibles.</p>
                )}

                <ul className="admin-pruebas-template-list list-unstyled mb-0">
                  {templates.map((tpl) => {
                    const ui = TEMPLATE_UI[tpl.id] || { icon: 'bi-envelope-fill', label: 'Mail' };
                    return (
                      <li key={tpl.id}>
                        <article className="admin-pruebas-template-tile">
                          <div className="admin-pruebas-template-icon" aria-hidden>
                            <i className={`bi ${ui.icon}`} />
                          </div>
                          <div className="admin-pruebas-template-copy">
                            <span className="admin-pruebas-template-pill">{ui.label}</span>
                            <h3 className="admin-pruebas-template-title">{tpl.title}</h3>
                            <p className="admin-pruebas-template-desc">{tpl.description}</p>
                          </div>
                          <div className="admin-pruebas-template-action">
                            <Button
                              variant="outline-dark"
                              className="admin-pruebas-btn-send"
                              disabled={!targetUser || sendingId === tpl.id}
                              onClick={() => handleSendSample(tpl.id)}
                            >
                              {sendingId === tpl.id ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Enviando…
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-send-fill me-2" aria-hidden />
                                  Enviar muestra
                                </>
                              )}
                            </Button>
                          </div>
                        </article>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>
          </div>
        )}

        {activeTab === TAB_PRODUCTO && (
          <div className="admin-pruebas-producto">
            <div className="admin-pruebas-max">
              <section className="admin-pruebas-panel admin-pruebas-panel--grant">
                <div className="admin-pruebas-panel-head admin-pruebas-panel-head--row">
                  <div>
                    <h2 className="admin-pruebas-panel-title mb-1">
                      <i className="bi bi-gift-fill me-2 text-primary" aria-hidden />
                      Asignar curso de prueba
                    </h2>
                    <p className="admin-pruebas-panel-lead mb-0">
                      Otorga acceso al curso y envía al usuario el correo de confirmación de compra (misma plantilla
                      que Mercado Pago), con número de orden e importe del producto en catálogo si existe. No genera
                      cobro ni orden en la base de pagos.
                    </p>
                  </div>
                  {loadingGrantCourses && <Spinner animation="border" className="text-primary" />}
                </div>

                <div className="admin-pruebas-grant-row">
                  <Form.Group className="admin-pruebas-grant-select">
                    <Form.Label className="text-secondary small fw-semibold text-uppercase letter-spacing-tight">
                      Curso
                    </Form.Label>
                    <Form.Select
                      size="lg"
                      className="admin-pruebas-input"
                      value={selectedCourseId}
                      onChange={(ev) => setSelectedCourseId(ev.target.value)}
                      disabled={loadingGrantCourses || grantCourses.length === 0}
                    >
                      <option value="">
                        {grantCourses.length === 0 && !loadingGrantCourses
                          ? 'No hay cursos en el catálogo'
                          : 'Elegí un curso…'}
                      </option>
                      {grantCourses.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.nombre}
                          {c.archivado ? ' (archivado)' : ''}
                          {!c.estado ? ' · inactivo' : ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <div className="admin-pruebas-grant-action">
                    <Button
                      variant="primary"
                      size="lg"
                      className="admin-pruebas-btn-grant"
                      disabled={!targetUser || !selectedCourseId || grantingCourse}
                      onClick={handleGrantCourse}
                    >
                      {grantingCourse ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Asignando…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check2-circle me-2" aria-hidden />
                          Asignar curso
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

const AdminPruebasPage = () => (
  <AdminNotificationsProvider>
    <AdminPruebasInner />
  </AdminNotificationsProvider>
);

export default AdminPruebasPage;
